import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { addTagToUserThunk } from '../settings/settingsSlice';
import type { RootState } from '../../reducers';

const MAINTENANCE_START = '2024-12-29T04:00:00Z'; // 9:30 IST Sunday
const MAINTENANCE_END = '2024-12-29T10:00:00Z'; // 15:30 IST Sunday

const MaintenanceBanner: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userTags = useSelector((state: RootState) => state.settings.user.tags);

  const maintenanceStartDate = new Date(MAINTENANCE_START);
  const maintenanceEndDate = new Date(MAINTENANCE_END);
  const now = new Date();

  // Create a unique key for this maintenance window
  const dismissalKey = `maintenance_${MAINTENANCE_START.slice(0, 10)}`;

  // Show banner if maintenance starts in less than 24 hours or if not dismissed
  const timeUntilMaintenance = maintenanceStartDate.getTime() - now.getTime();
  const isWithin24Hours =
    timeUntilMaintenance > 0 && timeUntilMaintenance < 24 * 60 * 60 * 1000;
  const lastDismissed = userTags[dismissalKey];
  const showBanner =
    isWithin24Hours || (!lastDismissed && timeUntilMaintenance > 0);

  if (!showBanner) {
    return null;
  }

  const handleDismiss = () => {
    dispatch(
      addTagToUserThunk({
        key: dismissalKey,
        value: new Date().toISOString(),
      }),
    );
  };

  const formatUTCTime = (date: Date) => {
    return date
      .toUTCString()
      .replace(/^.*?, /, '')
      .replace(/ GMT$/, '');
  };

  const formatLocalTime = (start: Date, end: Date) => {
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    };

    const startDay = start.getDate();
    const endDay = end.getDate();
    const sameDay = startDay === endDay;

    if (sameDay) {
      const date = start.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
      });
      const startTime = start.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
      });
      const endTime = end.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
      });
      const timezone =
        new Intl.DateTimeFormat(undefined, {
          timeZoneName: 'short',
        })
          .formatToParts(end)
          .find((part) => part.type === 'timeZoneName')?.value || '';

      return t('maintenance.time.sameDayFormat', {
        date,
        startTime,
        endTime,
        timezone,
      });
    }

    return t('maintenance.time.differentDayFormat', {
      start: start.toLocaleString(undefined, formatOptions),
      end: end.toLocaleString(undefined, formatOptions),
    });
  };

  return (
    <Alert className="relative border-yellow-500/20 bg-yellow-50/50 dark:bg-yellow-900/10">
      <AlertTitle className="font-semibold">
        {t('maintenance.title')}
      </AlertTitle>
      <AlertDescription className="text-sm text-muted-foreground mt-1">
        {t('maintenance.message', {
          time: formatLocalTime(maintenanceStartDate, maintenanceEndDate),
        })}
      </AlertDescription>
      <Button
        variant="ghost"
        size="sm"
        className="absolute right-2 top-2 h-6 w-6 p-0"
        onClick={handleDismiss}
        title={t('maintenance.notice', {
          start: formatUTCTime(maintenanceStartDate),
          end: formatUTCTime(maintenanceEndDate),
        })}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </Button>
    </Alert>
  );
};

export default MaintenanceBanner;
